'use client';
import React, { useState } from 'react';
import { Search, MapPin, ArrowRight, Shield, ChevronRight, Truck, Package, Clock } from 'lucide-react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';

interface DeliveryService {
  id: string;
  name: string;
  type: string;
  coverage: string;
  price: string;
  rating: number;
  deliveryTime: string;
  verified: boolean;
  description: string;
}

const SERVICES: DeliveryService[] = [
  { id: '1', name: 'Sendy Kenya', type: 'Same Day', coverage: 'Nairobi', price: 'From KES 200', rating: 4.7, deliveryTime: '2–4 hrs', verified: true, description: 'Fast same-day delivery within Nairobi. Track your parcel in real-time.' },
  { id: '2', name: 'G4S Courier', type: 'Nationwide', coverage: 'Kenya-wide', price: 'From KES 350', rating: 4.5, deliveryTime: '1–3 days', verified: true, description: 'Reliable nationwide courier with secure handling and insurance.' },
  { id: '3', name: 'DHL Kenya', type: 'International', coverage: 'Worldwide', price: 'From KES 2,500', rating: 4.8, deliveryTime: '2–5 days', verified: true, description: 'International and domestic express delivery with full tracking.' },
  { id: '4', name: 'Fargo Courier', type: 'Nationwide', coverage: 'Kenya-wide', price: 'From KES 300', rating: 4.3, deliveryTime: '1–2 days', verified: true, description: 'Affordable nationwide delivery with bus parcel and courier options.' },
  { id: '5', name: 'Boda Boda Express', type: 'Same Day', coverage: 'Nairobi, Mombasa', price: 'From KES 150', rating: 4.2, deliveryTime: '1–3 hrs', verified: false, description: 'Ultra-fast motorcycle delivery for small parcels within the city.' },
  { id: '6', name: 'Pickup Mtaani', type: 'Pickup Points', coverage: 'Nairobi', price: 'From KES 100', rating: 4.4, deliveryTime: 'Next day', verified: true, description: 'Convenient pickup points across Nairobi estates. Affordable and reliable.' },
];

const TYPES = ['All', 'Same Day', 'Nationwide', 'International', 'Pickup Points'];

export default function DeliveryPage() {
  const [search, setSearch] = useState('');
  const [type, setType] = useState('All');

  const filtered = SERVICES.filter(s => {
    const matchSearch = !search || s.name.toLowerCase().includes(search.toLowerCase()) || s.coverage.toLowerCase().includes(search.toLowerCase());
    const matchType = type === 'All' || s.type === type;
    return matchSearch && matchType;
  });

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ background: '#070C1A' }}>
        <div className="relative px-4 pt-8 pb-10 overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #0a0a1a 0%, #0d1520 50%, #070C1A 100%)' }}>
          <div className="absolute inset-0 opacity-20"
            style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #7C4DFF 0%, transparent 50%), radial-gradient(circle at 70% 30%, #00BCD4 0%, transparent 50%)' }} />
          <div className="relative max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-600 mb-4"
              style={{ background: 'rgba(124,77,255,0.15)', color: '#7C4DFF', border: '1px solid rgba(124,77,255,0.3)' }}>
              <Truck size={12} /> Delivery & Courier Services
            </div>
            <h1 className="text-3xl font-800 text-white mb-3 leading-tight">
              Fast <span style={{ color: '#7C4DFF' }}>Delivery</span> Across Kenya
            </h1>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Same-day, nationwide, and international courier services. Compare and book instantly.
            </p>
            <div className="relative max-w-md mx-auto">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.4)' }} />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search delivery services…"
                className="w-full pl-10 pr-4 py-3.5 rounded-2xl text-sm outline-none"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }} />
            </div>
          </div>
        </div>

        <div className="px-4 py-3 sticky top-0 z-20"
          style={{ background: 'rgba(7,12,26,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-4xl mx-auto flex items-center gap-2 overflow-x-auto pb-1">
            {TYPES.map(t => (
              <button key={t} onClick={() => setType(t)}
                className="px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
                style={{ background: type === t ? 'rgba(124,77,255,0.15)' : 'rgba(255,255,255,0.04)', color: type === t ? '#7C4DFF' : 'rgba(255,255,255,0.55)', border: `1px solid ${type === t ? 'rgba(124,77,255,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 pt-4 pb-8">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              <span style={{ color: '#7C4DFF', fontWeight: 700 }}>{filtered.length}</span> services available
            </span>
            <Link href="/login"
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-700"
              style={{ background: '#7C4DFF', color: '#fff' }}>
              Register as Courier <ArrowRight size={12} />
            </Link>
          </div>

          <div className="flex flex-col gap-3">
            {filtered.map(s => (
              <div key={s.id} className="p-4 rounded-2xl"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(124,77,255,0.12)', border: '1px solid rgba(124,77,255,0.2)' }}>
                    <Package size={20} style={{ color: '#7C4DFF' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-700 text-white">{s.name}</h3>
                          {s.verified && <Shield size={12} style={{ color: '#00C853' }} />}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <MapPin size={10} style={{ color: 'rgba(255,255,255,0.35)' }} />
                          <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>{s.coverage}</span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-800" style={{ color: '#7C4DFF' }}>{s.price}</p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <Clock size={10} style={{ color: 'rgba(255,255,255,0.35)' }} />
                          <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{s.deliveryTime}</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs mt-2 line-clamp-1" style={{ color: 'rgba(255,255,255,0.4)' }}>{s.description}</p>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-[10px] px-2 py-0.5 rounded-lg font-600"
                        style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)' }}>{s.type}</span>
                      <Link href="/login"
                        className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-700"
                        style={{ background: 'rgba(124,77,255,0.15)', color: '#7C4DFF', border: '1px solid rgba(124,77,255,0.25)' }}>
                        Book Now <ChevronRight size={11} />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-6 rounded-3xl text-center"
            style={{ background: 'linear-gradient(135deg, rgba(124,77,255,0.1), rgba(0,188,212,0.1))', border: '1px solid rgba(124,77,255,0.2)' }}>
            <h3 className="text-lg font-800 text-white mb-2">Offer Delivery Services</h3>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>Register your courier business and reach thousands of customers.</p>
            <Link href="/login"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-700 text-sm"
              style={{ background: '#7C4DFF', color: '#fff' }}>
              <Truck size={16} /> Register Courier
            </Link>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
